<script lang="ts">
	import Big from 'big.js';
	import { onMount } from 'svelte';
	import { formatBigWithLocale } from '../../helper/Parser';
	import { handleStoreError } from '../../stores/GeneralStore';

	export let id: string | null = null;
	export let value: Big | null = null;
	export let focusOnStart: boolean = false;
	export let placeholder: string | null = null;
	export let propertyName: string;
	export let positionDesign: boolean = false;
	export let disabled = false;
	export let rightAlign: boolean = false;
	export let min: Big = new Big(-99999999.999);
	export let max: Big = new Big(99999999.999);
	export let nullModus: boolean = false;
	export let numberOfDecimalPlaces: number = 2;
	export let callFunction: ((object: any) => Promise<any>) | null = null;
	export let errors: any = {};
	export let tabSwitcher: boolean = false;

	//We must adjust the start value if not matching to max or min
	if (min != null && value?.lt(min)) {
		if (nullModus && value == null) {
			value = null;
		} else {
			value = min;
		}
	} else if (max != null && value?.gt(max)) {
		value = max;
	}

	//Internal used value.
	$: textValue =
		value != null
			? formatBigWithLocale(value || new Big(0), null, numberOfDecimalPlaces)
			: nullModus
				? ''
				: formatBigWithLocale(new Big(0));

	let swissRegexMatch = new RegExp("^-?([0-9’']?)*[\\.]?[0-9]{0," + numberOfDecimalPlaces + '}$');

	let euRegexMatch = new RegExp('^-?([0-9\\.]?)*[,]?[0-9]{0,' + numberOfDecimalPlaces + '}$');

	let element: HTMLTextAreaElement | HTMLInputElement;

	onMount(() => {
		if (focusOnStart) {
			element.select();
		}
	});

	function handleBeforeInput(event: InputEvent) {
		const target = event.target as HTMLInputElement;
		if (target == null) return;

		//Build the next value
		const nextVal =
			target.value.substring(0, target.selectionStart || 0) +
			(event.data ?? '') +
			target.value.substring(target.selectionEnd || 0);

		let usedRegex = swissRegexMatch;

		/*
		if (!$locale?.endsWith('CH')) {
			usedRegex = euRegexMatch;
		}
		*/

		//Check if we do match the number
		if (nextVal.match(usedRegex) == null) {
			event.preventDefault();
			return;
		}
	}

	async function handleOnChange(event: CustomEvent) {
		const target = event.target as HTMLInputElement;
		if (target == null) return;

		if (callFunction) {
			try {
				let localValue = nullModus ? null : new Big(0);

				//Map it to a number
				if (target.value != '') {
					localValue = parseValue(target.value);
				}

				//We must set it to the event value.
				if (min != null && localValue?.lt(min)) {
					if (nullModus && localValue == null) {
						localValue = null;
					} else {
						localValue = min;
					}
				} else if (max != null && localValue?.gt(max)) {
					localValue = max;
				}

				const dynamicObject: any = {};
				dynamicObject[propertyName] = localValue;

				await callFunction(dynamicObject);

				//The reactivity is stuck at this point, so we have to help it
				textValue = '';
				//We need to reset the text value incase the parsed text aperase to be the same
				textValue =
					localValue == null ? '' : formatBigWithLocale(localValue, null, numberOfDecimalPlaces);

				//Map it to input
				value = localValue;
			} catch (error) {
				//Check if we have an error type
				handleStoreError('general.patch_failed', error);

				//We must reset the value
				target.value = formatBigWithLocale(value, null, numberOfDecimalPlaces);
			}
		} else {
			try {
				let localValue = nullModus ? null : new Big(0);

				//Map it to a number
				if (target.value != '') {
					localValue = parseValue(target.value);
				}

				//We can set it to the display value.
				if (min != null && localValue?.lt(min)) {
					if (value?.eq(min)) {
						//The reactivity is stuck at this point, so we have to help it
						textValue = '';
						//Set the right value back
						textValue = localValue == null ? '' : formatBigWithLocale(value);
					} else {
						value = min;
					}
				} else if (max != null && localValue?.gt(max)) {
					if (value?.eq(max)) {
						//The reactivity is stuck at this point, so we have to help it
						textValue = '';
						//Set the right value back
						textValue = localValue == null ? '' : formatBigWithLocale(value);
					} else {
						value = max;
					}
				} else {
					value = localValue;
				}
			} catch (error) {
				value = nullModus ? null : new Big(0);
			}
		}
	}

	function parseValue(textValue: string): Big {
		return new Big(textValue.replaceAll(/[^\d\.-]/g, ''));
		/*
		if ($locale?.endsWith('CH')) {
			return new Big(textValue.replaceAll(/[^\d\.-]/g, ''));
		} else {
			return new Big(textValue.replaceAll('.', '').replaceAll(',', '.'));
		}
			*/
	}

	function handleKeyDown(event: KeyboardEvent) {
		if (event.key == 'Enter') {
			element.blur();
		}
	}
</script>

<div class:positionDesign>
	{#if positionDesign}
		<textarea
			{id}
			data-testid={propertyName}
			rows="1"
			autocomplete="off"
			{disabled}
			class="p-2 w-full h-full border-gray outline-none bg-transparent resize-none"
			class:switcher={tabSwitcher}
			class:text-right={rightAlign}
			on:beforeinput={handleBeforeInput}
			on:change={handleOnChange}
			on:change
			on:blur
			on:focus={() => element.select()}
			bind:this={element}
			value={textValue}
			on:keydown={handleKeyDown}
			{placeholder}
		></textarea>
	{:else}
		<input
			{id}
			data-testid={propertyName}
			autocomplete="off"
			{disabled}
			inputmode="decimal"
			class:switcher={tabSwitcher}
			class:text-right={rightAlign}
			on:beforeinput={handleBeforeInput}
			on:change={handleOnChange}
			on:change
			on:blur
			on:focus={() => element.select()}
			bind:this={element}
			value={textValue}
			on:keydown={handleKeyDown}
			{placeholder}
		/>
	{/if}

	{#if errors[propertyName]}
		<p class="text-red dark:text-orange text-sm">{errors[propertyName]}</p>
	{/if}
</div>
